/**
 * Copyright (c) 2024 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { v4 } from 'uuid';
import ZoomVideo, {
  VideoClient as ZoomVideoClient,
  LocalAudioTrack as ZoomLocalAudioTrack,
  LocalVideoTrack as ZoomLocalVideoTrack,
  Stream as ZoomStream,
  Participant as ZoomParticipant,
  event_network_quality_change as ZoomNetworkQualityChangeEvent,
  event_device_permission_change as ZoomDevicePermissionChangeEvent,
  VideoQuality as ZoomVideoQuality,
} from '@zoom/videosdk';
import { datadogRum } from '@datadog/browser-rum';
import { notaryAPI } from '../../../api/notary';
import { participantAPI } from '../../../api/participant';

import { logEvent } from '../utils';
import { VideoProviderName } from '../types';
import { toUpper } from '@utils/lodash';
import { MemoizedZoomParticipantStrip } from '@components/videoProvider/zoom/ZoomParticipantStrip/ZoomParticipantStrip';
import { MemoizedZoomVideoTrack } from '@components/videoProvider/zoom/VideoTrack/ZoomVideoTrack';
import IParticipant = VideoProvider.IParticipant;
import { diagnostics } from '@utils/diagnostics';
import {
  calculateNetworkQualityScore,
  PreflightQualityScore,
} from '@components/form/TaskInputs/NetworkConnection/Preflight/getPreflightQualityScore';
import { runSpeedAndLatencyTest } from '@components/form/TaskInputs/NetworkConnection/Preflight/runSpeedAndLatencyTest';

export class ZoomVideoProvider implements VideoProvider.IProvider {
  name = VideoProviderName.ZOOM;
  zoomClient: typeof ZoomVideoClient;
  zoomSession: typeof ZoomStream | null;
  private dominantSpeakerListener: ((payload: any) => void) | null;
  private deviceChangeListener: ((payload: Parameters<typeof ZoomDevicePermissionChangeEvent>[0]) => void) | null;
  private networkQualityListener: ((payload: Parameters<typeof ZoomNetworkQualityChangeEvent>[0]) => void) | null;

  constructor() {
    ZoomVideo.preloadDependentAssets();
    this.zoomClient = ZoomVideo.createClient();
    this.zoomClient.init('en-US', 'Global', {
      patchJsMedia: true,
      enforceMultipleVideos: true,
      // Quickly leave the session when refreshing or closing the page
      // ref: https://marketplacefront.zoom.us/sdk/custom/web/interfaces/InitOptions.html#leaveOnPageUnload
      leaveOnPageUnload: true,
    });
    this.zoomSession = null;
    this.dominantSpeakerListener = null;
    this.deviceChangeListener = null;
    this.networkQualityListener = null;
  }

  async connect(
    token: string,
    callback: () => void,
    meta: {
      callId?: string;
      userFullName?: string;
      videoRoomId?: string;
      isNotary?: boolean;
    },
  ): Promise<{ disconnect: () => void; session: VideoProvider.IRoom }> {
    const { callId, userFullName, videoRoomId, isNotary } = meta;
    // we are using the name for showing it up in the closed captioning
    // so, displaying only the FirstName should be good enough
    const firstName = userFullName?.split(' ')[0];

    // eslint-disable-next-line no-console
    console.log('[ZOOM] init', { callId, videoRoomId, isNotary });
    datadogRum.addAction('[ZOOM] init', { callId, videoRoomId, isNotary });
    try {
      await this.zoomClient.join(callId || 'bsr', token, firstName || 'user', '');
      this.zoomSession = this.zoomClient.getMediaStream();
      this.zoomSession.subscribeVideoStatisticData();
      this.zoomSession.subscribeAudioStatisticData();

      const sessionInfo = this.zoomClient.getSessionInfo();
      if (isNotary && videoRoomId && sessionInfo.sessionId !== videoRoomId) {
        // zoom creates a new sessionId on their end whenever there are no active participants in the meeting
        // this can happen when the last participant leaves the meeting and the meeting is still active
        // log an error if the zoom's sessionId does not match the sessionId stored in BSR
        // and update the sessionId in BSR with the new sessionId from zoom (also save the previous sessionId)
        diagnostics.error(new Error('Zoom session id does not match the video room id'), {
          sdkSession: sessionInfo.sessionId,
          bsrSessiom: videoRoomId,
        });
        await notaryAPI.calls.saveAdditionVideoRoomId(callId!, { newVideoRoomId: sessionInfo.sessionId });
      }
      // eslint-disable-next-line no-console
      console.log('[ZOOM] connect', { sessionInfo });
      datadogRum.addAction('[ZOOM] connect', { sessionId: sessionInfo.sessionId });
    } catch (err) {
      logEvent('error', 'Zoom connection failed', false, { err });
      throw err;
    }
    const disconnect = () => this.leaveSession();

    // invoke the onConnectionChange listener
    this.zoomClient.on('connection-change', payload => {
      // TODO: (ZOOMSDK) - handle closing the connection ?
      if (payload.state === 'Closed') {
        callback();
        ZoomVideo.destroyClient();
        logEvent('info', 'Zoom connection closed', false, { payload });
      } else if (payload.state === 'Fail') {
        logEvent('error', 'Zoom connection failed', false, { payload });
      } else {
        // istanbul ignore next
        logEvent('info', 'Zoom connection state', false, { payload });
      }
    });

    // Returning empty object for the Zoom session to enforce the abstraction of the video provider interface.
    // To prevent developers from accidentally relying on the Zoom session object and to promote consistency
    // across video providers, we return {} here. This ensures that all interactions with the video provider are
    // done through abstracted methods, making the implementation details of videoProvider opaque and easier to manage.
    // Unlike Twilio, where the session object is accessible and used directly, Zoom's session object is not used directly.
    // TODO: (ZOOMSDK) Eventually, Twilio's session will also be removed, and only abstract methods will be used across all providers.
    return { session: {} as VideoProvider.IRoom, disconnect };
  }

  /**
   * Disconnect from the Zoom session
   * This method should be called when the user leaves the room or the tab/browser is closed
   * It is necessary to prevent the test video session from being left open
   */
  leaveSession() {
    datadogRum.addAction('[ZOOM] leaveSession');
    this.zoomClient.leave();
  }

  isCallConnected() {
    return Boolean(this.zoomSession);
  }

  async createAndUnmuteLocalAudioTrack(deviceId?: string): Promise<VideoProvider.ILocalAudioTrack> {
    const localTrack: ZoomLocalAudioTrack = ZoomVideo.createLocalAudioTrack(deviceId);

    (localTrack as any).name = v4();
    (localTrack as any).kind = 'audio';

    Object.defineProperties(localTrack, {
      isEnabled: {
        get() {
          return !!this.isAudioStarted;
        },
        configurable: true,
        enumerable: true,
      },
    });

    await localTrack.start();
    await localTrack.unmute();

    return localTrack as unknown as VideoProvider.ILocalAudioTrack;
  }

  decorateLocalVideoTrack(localTrack: ZoomLocalVideoTrack): VideoProvider.ILocalVideoTrack {
    (localTrack as any).name = `camera-${Date.now()}`;
    (localTrack as any).kind = 'video';

    Object.defineProperties(localTrack, {
      attach: {
        async value(el: HTMLElement) {
          !this.isVideoStarted && (await this.start(el));
        },
        configurable: true,
        enumerable: true,
      },
      detach: {
        async value() {
          this.isVideoStarted && (await this.stop());
        },
        configurable: true,
        enumerable: true,
      },
      restart: {
        // TODO: (ZOOMSDK) Revisit the actual purpose and usage of deviceId.exact.
        async value(constraints?: MediaTrackConstraints) {
          let deviceId: string | null;
          if (typeof constraints?.deviceId === 'string') {
            deviceId = constraints.deviceId;
          } else if (
            typeof constraints?.deviceId === 'object' &&
            'exact' in constraints.deviceId &&
            typeof constraints.deviceId.exact === 'string'
          ) {
            deviceId = constraints.deviceId.exact;
          } else {
            deviceId = null;
          }
          deviceId && (await this.switchCamera(deviceId));
        },
        configurable: true,
        enumerable: true,
      },
    });

    return localTrack as unknown as VideoProvider.ILocalVideoTrack;
  }

  async createLocalTracks(
    selectedAudioDeviceId: string | null,
    selectedVideoDeviceId: string | null,
    isCameraPermissionDenied?: boolean,
    isMicrophonePermissionDenied?: boolean,
  ): Promise<{
    audioTrack?: VideoProvider.ILocalAudioTrack;
    videoTrack?: VideoProvider.ILocalVideoTrack;
    updatedVideoDeviceId?: string;
    updatedAudioDeviceId?: string;
  }> {
    const DEFAULT_PROPS_ON_PERMISSION_DENIED = {
      deviceId: 'default',
      name: 'default',
    } as VideoProvider.ILocalVideoTrack;
    // setting a default value for isCameraPermissionDenied and isMicrophonePermissionDenied
    const audioTrack = !isMicrophonePermissionDenied
      ? await this.createLocalAudioTrack(selectedAudioDeviceId)
      : DEFAULT_PROPS_ON_PERMISSION_DENIED;
    const videoTrack = !isCameraPermissionDenied
      ? await this.createLocalVideoTrack(selectedVideoDeviceId)
      : DEFAULT_PROPS_ON_PERMISSION_DENIED;

    const updatedVideoDeviceId = videoTrack.deviceId!;
    const updatedAudioDeviceId = audioTrack.deviceId!;

    // eslint-disable-next-line no-console
    console.log('ZoomVideoProvider.createLocalTracks()', { audioTrack, videoTrack, updatedVideoDeviceId });

    return { audioTrack, videoTrack, updatedVideoDeviceId, updatedAudioDeviceId };
  }

  async createLocalVideoTrack(selectedVideoDeviceId: string | null) {
    const devices = await ZoomVideo.getDevices();
    const videoDevices = devices.filter(device => {
      return device.kind === 'videoinput';
    });
    const [defaultVideoDevice] = videoDevices;

    const selectedVideoDeviceExists = videoDevices.some(device => device.deviceId === selectedVideoDeviceId);
    const deviceId = selectedVideoDeviceExists ? selectedVideoDeviceId! : defaultVideoDevice.deviceId;

    return this.decorateLocalVideoTrack(ZoomVideo.createLocalVideoTrack(deviceId));
  }

  async createLocalAudioTrack(selectedAudioDeviceId: string | null) {
    const devices = await ZoomVideo.getDevices();
    const microphoneDevices = devices.filter(device => {
      return device.kind === 'audioinput';
    });
    const [defaultMicrophoneDevice] = microphoneDevices;

    const selectedAudioDeviceIdExists = microphoneDevices.some(device => device.deviceId === selectedAudioDeviceId);
    const deviceId = selectedAudioDeviceIdExists ? selectedAudioDeviceId! : defaultMicrophoneDevice?.deviceId;

    return await this.createAndUnmuteLocalAudioTrack(deviceId);
  }

  getMediaTrackOrStream(track?: VideoProvider.ITrack): MediaStream | MediaStreamTrack | undefined {
    return track?.audioStream || track?.videoStream;
  }

  // Zoom does not have a track started event. It auto-populates track?.audioStream
  onTrackStarted(_track: VideoProvider.ITrack, _callback: () => void) {}
  // Zoom does not have a track stopped event. It auto-populates track?.audioStream
  onTrackStopped(_track: VideoProvider.ITrack, _callback: () => void) {}

  isCameraFrontFacing(_mediaStreamOrTrack: MediaStream | MediaStreamTrack): boolean {
    // TODO: (ZOOMSDK) - there is no direct way to fetch the camera facing mode. Investigate how to fetch for zoom
    return false;
  }

  streamLocalVideo(cameraId?: string) {
    return this.zoomSession?.startVideo({
      cameraId: cameraId || undefined,
      hd: true,
      fullHd: true,
      originalRatio: true,
    });
  }

  streamLocalAudio(microphoneId?: string) {
    return this.zoomSession?.startAudio({ microphoneId: microphoneId || undefined });
  }

  stopLocalVideo() {
    return this.zoomSession?.stopVideo();
  }

  stopLocalAudio() {
    return this.zoomSession?.stopAudio();
  }

  async attachVideoToVideoPlayer(userId: number, videoPlayer: VideoProvider.ZoomVideoPlayer) {
    return this.zoomSession?.attachVideo(userId, ZoomVideoQuality.Video_360P, videoPlayer);
  }

  async detachVideoFromVideoPlayer(userId: number) {
    if (!this.zoomClient.getSessionInfo()?.isInMeeting) {
      // Do not detach video if the user is not in the meeting
      return;
    }
    return this.zoomSession?.detachVideo(userId);
  }

  async streamSwitchCamera(deviceId: string) {
    await this.zoomSession?.switchCamera(deviceId);
    return;
  }

  async streamSwitchMicrophone(
    deviceId: string,
    localAudioTrack: VideoProvider.ILocalAudioTrack,
    getLocalAudioTrack: () => Promise<VideoProvider.ILocalAudioTrack>,
  ) {
    await localAudioTrack.stop();
    await getLocalAudioTrack();
    await this.zoomSession?.switchMicrophone(deviceId);
  }

  /**
   * Related issue for this implementation
   * https://devforum.zoom.us/t/firefox-individual-elements-not-working-with-multiple-videos/106655
   * */
  isSupportMultipleVideos() {
    return !!this.zoomSession?.isSupportMultipleVideos();
  }

  async runPreflightTest(
    roomId: string,
    updatePreflightReport: (
      preflightReport: VideoProvider.PreflightResults<VideoProvider.ZoomPreflightReport, PreflightQualityScore>,
    ) => void,
  ) {
    logEvent('info', '[ZOOM] Running Preflight Test for Room', true, { roomId });

    const preflightReport: VideoProvider.ZoomPreflightReport = {
      testTiming: { startTime: new Date(), endTime: new Date() },
      networkTiming: { downloadSpeed: 0, latency: 0 },
    };

    try {
      // Step 1: Measure Download Speed and Latency
      const { downloadSpeed, latency } = await runSpeedAndLatencyTest();
      preflightReport.networkTiming.downloadSpeed = parseFloat(downloadSpeed);
      preflightReport.networkTiming.latency = parseFloat(latency);

      logEvent('info', '[ZOOM] Network Speed Test Completed', true, { roomId, preflightReport });

      // Step 2: Assess Network Quality
      const qualityScore = calculateNetworkQualityScore(
        preflightReport.networkTiming.downloadSpeed,
        preflightReport.networkTiming.latency,
      );
      preflightReport.testTiming.endTime = new Date();

      // Step 3: Update Preflight Report
      updatePreflightReport({
        preflightReport,
        qualityScore,
      });

      logEvent('info', '[ZOOM] Preflight Test Completed', true, { roomId, preflightReport, qualityScore });
    } catch (err) {
      logEvent('error', '[ZOOM] Preflight Test Failed', true, { roomId, preflightReport, err });
      updatePreflightReport({
        preflightReport,
        qualityScore: 0,
      });
    }
  }

  /**
   * Participants related methods
   */
  private _participantMapper = (user: Partial<ZoomParticipant>): Partial<IParticipant> => {
    return {
      sid: String(user.userId),
      identity: user.userIdentity,
      ...user,
    };
  };
  getParticipants(): IParticipant[] {
    return this.zoomClient.getAllUser().map(this._participantMapper) as unknown as IParticipant[];
  }
  getCurrentUserId(): number {
    return this.zoomClient.getCurrentUserInfo()?.userId;
  }

  onParticipantConnected(callback: (participants?: IParticipant[]) => void) {
    this.zoomClient.on('user-added', callback);
  }
  onParticipantDisconnected(callback: (participants?: IParticipant[]) => void) {
    this.zoomClient.on('user-removed', callback);
  }
  onParticipantUpdated(callback: (participants?: IParticipant[]) => void) {
    this.zoomClient.on('user-updated', callback);
  }

  offParticipantConnected(callback: (participants?: IParticipant[]) => void) {
    this.zoomClient.off('user-added', callback);
  }
  offParticipantDisconnected(callback: (participants?: IParticipant[]) => void) {
    this.zoomClient.off('user-removed', callback);
  }
  offParticipantUpdated(callback: (participants?: IParticipant[]) => void) {
    this.zoomClient.off('user-updated', callback);
  }

  onDominantSpeakerChanged(callback: (participant: IParticipant) => void) {
    this.dominantSpeakerListener = (payload: any) => {
      // Zoom might return multiple dominant speakers, but we only need to show one
      callback(this._participantMapper(payload[0]) as unknown as IParticipant);
    };

    this.zoomClient.on('active-speaker', this.dominantSpeakerListener);
  }

  offDominantSpeakerChanged() {
    if (this.dominantSpeakerListener) {
      this.zoomClient.off('active-speaker', this.dominantSpeakerListener);
      this.dominantSpeakerListener = null; // Reset after removing
    }
  }

  isDominantSpeaker(participant?: IParticipant, dominantSpeaker?: IParticipant | null) {
    return participant?.userId === dominantSpeaker?.userId;
  }

  onDevicePermissionsChanged(
    permissionDeniedCallback?: (device: string) => void,
    permissionGrantedCallback?: (device: string) => void,
  ) {
    this.deviceChangeListener = (payload: Parameters<typeof ZoomDevicePermissionChangeEvent>[0]) => {
      const device = toUpper(payload.name);

      switch (payload.state) {
        case 'denied':
          permissionDeniedCallback && permissionDeniedCallback(device);
          break;
        case 'granted':
          permissionGrantedCallback && permissionGrantedCallback(device);
          break;
        default:
      }
    };

    this.zoomClient.on('device-permission-change', this.deviceChangeListener);
  }

  offDevicePermissionsChanged() {
    if (this.deviceChangeListener) {
      this.zoomClient.off('device-permission-change', this.deviceChangeListener);
      this.deviceChangeListener = null; // Reset the listener after removing
    }
  }

  onNetworkQualityLevelChanged(
    participant: VideoProvider.IParticipant,
    callback: (networkQualityLevel: number) => void,
  ) {
    this.networkQualityListener = (payload: Parameters<typeof ZoomNetworkQualityChangeEvent>[0]) => {
      if (payload.type === 'uplink' || participant.userId !== payload.userId) {
        return;
      }

      callback(payload.level);
    };

    this.zoomClient.on('network-quality-change', this.networkQualityListener);
  }

  offNetworkQualityLevelChanged() {
    if (this.networkQualityListener) {
      this.zoomClient.off('network-quality-change', this.networkQualityListener);
      this.networkQualityListener = null; // Reset the listener after removing
    }
  }

  /**
   * Closed Captioning related methods
   */
  isClosedCaptioningSupported() {
    return true;
  }
  async startClosedCaptioning(): Promise<void> {
    const liveTranscriptionClient = this.zoomClient.getLiveTranscriptionClient();
    // eslint-disable-next-line no-console
    console.log('[ZOOM] startClosedCaptioning', liveTranscriptionClient);
    void participantAPI.rooms.sendBrowserLog({
      message: '[Zoom] startClosedCaptioning',
    });
    await liveTranscriptionClient.startLiveTranscription();
  }
  onClosedCaptioningMessage(callback: (message: VideoProvider.ZoomCaptionPayload) => void) {
    // eslint-disable-next-line no-console
    console.log('[ZOOM] onClosedCaptioningMessage');
    this.zoomClient.on('caption-message', callback);
  }
  offloadClosedCaptioningMessage(callback: (message: VideoProvider.ZoomCaptionPayload) => void) {
    // eslint-disable-next-line no-console
    console.log('[ZOOM] offloadClosedCaptioningMessage');
    this.zoomClient.off('caption-message', callback);
  }
  disableClosedCaptioning(): Promise<void> {
    throw new Error('Method not implemented.');
  }

  /**
   * Components and CSS overrides
   */
  getParticipantStripComponent(): React.NamedExoticComponent<{ isParticipantsTabView?: boolean }> {
    return MemoizedZoomParticipantStrip;
  }
  getParticipantTrackComponent() {
    return MemoizedZoomVideoTrack;
  }
  shouldOverrideAspectRatio() {
    return true;
  }
  shouldReplaceParticipantTabWithStrip() {
    return true;
  }
}
